import React, { useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TextInput, TouchableOpacity,
  ActivityIndicator, Platform,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { FONTS } from '../../constants/config';
import { PageContainer, Navbar, Breadcrumb, Toast } from '../../components';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { activityOutputsApi } from '../../services/apiService';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { createShadow } from '../../utils/styles';

type RouteProps = RouteProp<MainStackParamList, 'AddOutput'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

interface FolderItem {
  id: string;
  name: string;
}

export default function AddOutputScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { colors } = useTheme();
  const { language } = useLanguage();
  const { isMobile } = useResponsive();
  const isRTL = language === 'ar';
  const containerPadding = isMobile ? 12 : 24;

  const projectId = route.params?.projectId;
  const specializationId = (route.params as any)?.specializationId;
  const activityId = (route.params as any)?.activityId;

  const [name, setName] = useState('');
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverFileName, setCoverFileName] = useState('');
  const [folders, setFolders] = useState<FolderItem[]>([{ id: 'default', name: isRTL ? 'مجلد 1' : 'Folder 1' }]);
  const [foldersExpanded, setFoldersExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');

  const handlePickCover = () => {
    if (Platform.OS !== 'web') return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg';
    input.onchange = (e: any) => {
      const file = e.target.files?.[0];
      if (file) {
        setCoverFile(file);
        setCoverFileName(file.name);
      }
    };
    input.click();
  };

  const handleAddFolder = () => {
    setFolders(prev => [...prev, { id: Date.now().toString(), name: '' }]);
  };

  const handleRemoveFolder = (id: string) => {
    setFolders(prev => prev.length <= 1 ? prev : prev.filter(f => f.id !== id));
  };

  const handleFolderNameChange = (id: string, newName: string) => {
    setFolders(prev => prev.map(f => f.id === id ? { ...f, name: newName } : f));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setToastType('error');
      setToastMsg(isRTL ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    if (!projectId) {
      setToastType('error');
      setToastMsg(isRTL ? 'لا يوجد مشروع مرتبط' : 'No linked project');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', name.trim());
      if (projectId) formData.append('project', String(projectId));
      if (specializationId) formData.append('specialization', String(specializationId));
      if (activityId) formData.append('activity', String(activityId));
      formData.append('output_type', 'album');
      if (coverFile) {
        formData.append('file', coverFile);
      }
      // Store folder names as JSON in a custom field or handle server-side
      const folderNames = folders.filter(f => f.name.trim()).map(f => f.name.trim());
      if (folderNames.length > 0) {
        formData.append('folder_names', JSON.stringify(folderNames));
      }
      await activityOutputsApi.create(formData);
      setToastType('success');
      setToastMsg(isRTL ? 'تم حفظ المخرج بنجاح' : 'Output saved successfully');
      setTimeout(() => navigation.goBack(), 800);
    } catch (err) {
      console.log('[AddOutput] save failed:', err);
      setToastType('error');
      setToastMsg(isRTL ? 'فشل في حفظ المخرج' : 'Failed to save output');
    }
    setSaving(false);
  };

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        <View style={[styles.container, { padding: containerPadding }]}>
          <Breadcrumb
            items={[
              { label: isRTL ? 'الرئيسية' : 'Home' },
              { label: isRTL ? 'مبادرة مواهب' : 'Talents', route: 'Talents' },
              { label: isRTL ? 'تفاصيل المشروع' : 'Project Details', route: 'ProjectDetail' },
              { label: isRTL ? 'إضافة مخرج' : 'Add Output' },
            ]}
          />

          {/* Main Card */}
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: '#e2e8f0' }, createShadow(0, 1, 3, '#000', 0.1)]}>
            <Text style={[styles.cardTitle, { color: colors.textPrimary }]}>
              {isRTL ? 'معلومات المخرج' : 'Output Information'}
            </Text>

            {/* Basic Info Section */}
            <View style={[styles.section, { backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' }]}>
              <Text style={[styles.sectionTitle, { color: colors.textPrimary }]}>
                {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
              </Text>

              {/* Name Field */}
              <View style={styles.field}>
                <Text style={[styles.label, { color: colors.textPrimary }]}>
                  <Text style={{ color: '#d62728' }}>*</Text>
                  {isRTL ? ' الاسم' : ' Name'}
                </Text>
                <TextInput
                  style={[styles.input, { backgroundColor: '#fff', borderColor: '#e2e8f0', color: colors.textPrimary }]}
                  placeholder={isRTL ? 'اختر الاسم' : 'Enter name'}
                  placeholderTextColor="#64748b"
                  value={name}
                  onChangeText={setName}
                  textAlign={isRTL ? 'right' : 'left'}
                />
              </View>

              {/* Cover Image Field */}
              <View style={styles.field}>
                <Text style={[styles.label, { color: colors.textPrimary }]}>
                  {isRTL ? 'صورة الغلاف (اختياري)' : 'Cover Image (optional)'}
                </Text>
                <TouchableOpacity
                  style={[styles.input, styles.fileInput, { backgroundColor: '#fff', borderColor: '#e2e8f0' }]}
                  onPress={handlePickCover}
                >
                  <View style={[styles.fileInputRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                    <View style={styles.chooseFileBtn}>
                      <Text style={styles.chooseFileBtnText}>
                        {isRTL ? 'اختر ملف' : 'Choose file'}
                      </Text>
                    </View>
                    <Text style={[styles.fileNameText, { color: coverFileName ? colors.textPrimary : '#64748b' }]} numberOfLines={1}>
                      {coverFileName || (isRTL ? 'لم يتم اختيار ملف' : 'No file chosen')}
                    </Text>
                  </View>
                </TouchableOpacity>
                <View style={[styles.hintRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <Ionicons name="information-circle-outline" size={18} color="#64748b" />
                  <Text style={styles.hintText}>
                    {isRTL
                      ? 'صورة الغلاف يجب أن تكون بمقاس بين 256*1024 و 400*1600، تقبل صيغة PNG أو JPEG'
                      : 'Cover image should be between 256x1024 and 400x1600, PNG or JPEG format'}
                  </Text>
                </View>
              </View>
            </View>

            {/* Folders Section */}
            <View style={[styles.section, { backgroundColor: '#f1f5f9', borderColor: '#e2e8f0' }]}>
              <View style={[styles.sectionHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                <Text style={[styles.sectionTitle, { flex: 1, textAlign: isRTL ? 'right' : 'left' }]}>
                  {isRTL ? 'المجلدات' : 'Folders'}
                </Text>
                <TouchableOpacity onPress={() => setFoldersExpanded(!foldersExpanded)} style={styles.toggleBtn}>
                  <Ionicons name={foldersExpanded ? 'chevron-up' : 'chevron-down'} size={18} color="#64748b" />
                </TouchableOpacity>
              </View>

              {foldersExpanded && (
                <View style={styles.foldersContainer}>
                  {folders.map((folder) => (
                    <View key={folder.id} style={[styles.folderRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                      <View style={styles.folderCard}>
                        <View style={[styles.folderCardInner, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                          <View style={{ flex: 1 }}>
                            <Text style={[styles.label, { color: colors.textPrimary }]}>
                              <Text style={{ color: '#d62728' }}>*</Text>
                              {isRTL ? ' اسم المجلد' : ' Folder name'}
                            </Text>
                            <TextInput
                              style={[styles.input, { backgroundColor: '#fff', borderColor: '#e2e8f0', color: colors.textPrimary }]}
                              value={folder.name}
                              onChangeText={(text) => handleFolderNameChange(folder.id, text)}
                              textAlign={isRTL ? 'right' : 'left'}
                            />
                          </View>
                          {folders.length > 1 && (
                            <TouchableOpacity
                              onPress={() => handleRemoveFolder(folder.id)}
                              style={styles.deleteFolderBtn}
                            >
                              <Ionicons name="trash-outline" size={18} color="#64748b" />
                            </TouchableOpacity>
                          )}
                        </View>
                      </View>
                      <View style={styles.dragHandle}>
                        <Ionicons name="menu" size={16} color="#64748b" />
                      </View>
                    </View>
                  ))}

                  <TouchableOpacity
                    style={[styles.addFolderBtn, createShadow(0, 1, 3, '#000', 0.1)]}
                    onPress={handleAddFolder}
                  >
                    <Text style={styles.addFolderText}>
                      {isRTL ? 'إضافة مجلد' : 'Add Folder'}
                    </Text>
                    <Ionicons name="add" size={14} color="#414042" />
                  </TouchableOpacity>
                </View>
              )}
            </View>

            {/* Footer Buttons */}
            <View style={[styles.footer, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
              <TouchableOpacity
                style={[styles.cancelBtn, { borderColor: '#cbd5e1' }, createShadow(0, 1, 3, '#000', 0.1)]}
                onPress={() => navigation.goBack()}
              >
                <Text style={styles.cancelBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.saveBtn, saving && { opacity: 0.6 }]}
                onPress={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.saveBtnText}>{isRTL ? 'حفظ' : 'Save'}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  container: { flex: 1 },
  card: {
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    gap: 20,
    marginTop: 12,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  section: {
    borderRadius: 4,
    borderWidth: 1,
    padding: 24,
    gap: 24,
  },
  sectionHeader: {
    alignItems: 'center',
    gap: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    textAlign: 'right',
  },
  toggleBtn: {
    width: 40, height: 40, borderRadius: 8,
    borderWidth: 1, borderColor: '#e2e8f0',
    backgroundColor: 'rgba(51,65,85,0.05)',
    alignItems: 'center', justifyContent: 'center',
  },
  field: {
    gap: 6,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 7.5,
    minHeight: 36,
    fontSize: 14,
    fontFamily: FONTS.regular,
  },
  fileInput: {
    justifyContent: 'center',
  },
  fileInputRow: {
    alignItems: 'center',
    gap: 8,
  },
  chooseFileBtn: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minHeight: 24,
  },
  chooseFileBtnText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#0f172a',
  },
  fileNameText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    flex: 1,
    textAlign: 'right',
  },
  hintRow: {
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  hintText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    flex: 1,
    textAlign: 'right',
  },
  foldersContainer: {
    gap: 24,
    alignItems: 'center',
  },
  folderRow: {
    width: '100%',
    gap: 12,
    alignItems: 'center',
  },
  folderCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    padding: 12,
  },
  folderCardInner: {
    gap: 24,
    alignItems: 'flex-end',
  },
  deleteFolderBtn: {
    width: 40, height: 40, borderRadius: 8,
    borderWidth: 1, borderColor: '#e2e8f0',
    backgroundColor: 'rgba(51,65,85,0.05)',
    alignItems: 'center', justifyContent: 'center',
  },
  dragHandle: {
    paddingTop: 24,
  },
  addFolderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: '#cbd5e1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    backgroundColor: '#fff',
    minHeight: 36,
  },
  addFolderText: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: '#414042',
  },
  footer: {
    gap: 8,
    paddingTop: 16,
  },
  cancelBtn: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    backgroundColor: 'rgba(255,255,255,0.1)',
    minHeight: 36,
  },
  cancelBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'center',
  },
  saveBtn: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 7.5,
    minHeight: 36,
    justifyContent: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.bold,
    color: '#f8fafc',
    textAlign: 'center',
  },
});
